import { addAlpha, Colors } from "../../../utils/colors";
import { useDispatch, useSelector } from "react-redux";
import { getAccountState } from "../../../redux/slices/accountSlice";
import StandardBorderedContainer from "../../Containers/StandardBorderedContainer";
import { IconButton } from "@mui/material";
import {
  PlanTypeV4,
  NotificationType,
  OnboardingFlowType,
} from "@markit/common.types";
import { useCallback, useMemo, useState } from "react";
import RectangleButton from "../../Buttons/RectangleButton";
import { API } from "../../../API";
import {
  hasSubscription,
  hasSubscriptionUnlockedAdvancedData,
} from "@markit/common.utils";
import { HorizontalDivider } from "../../Dividers/HorizontalDivider";
import CustomTextField from "../../CustomTextField";
import { Icon } from "@iconify/react";
import { DropdownMenu, DropdownMenuItem } from "../../DropdownMenu";
import { showNotificationBanner } from "../../../utils/notificationUtils";
import ConfirmDeleteModal from "../../Containers/ConfirmPopups/ConfirmDeleteModal";
import LabelButton from "../../Buttons/LabelButton";
import DropdownArrowIcon from "../../DropdownsAndTabs/DropdownArrowIcon";
import EmptyStateButtonSubscription from "../../Buttons/EmptyState/EmptyStateButtonSubscription";
import OnboardingTourStep from "../../Onboarding/OnboardingTourStep";
import { MCP_API_ONBOARDING_STEPS } from "../../../utils/onboardingFlowUtils";

type ApiManagementPanelProps = {
  goToMcpTab: () => void;
  /** Shows the MCP & API tour's last step, after its MCP tab steps. */
  showMcpTourFinalStep?: boolean;
};

const ApiManagementPanel = (props: ApiManagementPanelProps) => {
  const { goToMcpTab, showMcpTourFinalStep } = props;
  const { accountData } = useSelector(getAccountState).account;
  const dispatch = useDispatch();
  const [apiKeyId, setApiKeyId] = useState<string>("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [loadingGenerate, setLoadingGenerate] = useState(false);

  const styles = {
    noticeContainer: {
      borderRadius: 12,
      padding: 10,
      backgroundColor: addAlpha(Colors.ORANGE1, 0.1),
      border: `1px solid ${Colors.ORANGE1}`,
    },
  };

  const handleClickShowApiKey = useCallback(
    () => setShowApiKey(!showApiKey),
    [showApiKey]
  );

  const generateApiKey = useCallback(async () => {
    setLoadingGenerate(true);
    const response = await API.apiKeyManagement.createApiKey({});
    setApiKeyId(response.newApiKeyId);
    showNotificationBanner(
      dispatch,
      "API Key Generated!",
      NotificationType.AFFIRMATIVE
    );
    setLoadingGenerate(false);
  }, [dispatch]);

  const regenerateApiKey = useCallback(async () => {
    const response = await API.apiKeyManagement.regenerateApiKey({});
    setApiKeyId(response.newApiKeyId);
    showNotificationBanner(
      dispatch,
      "API Key Regenerated!",
      NotificationType.AFFIRMATIVE
    );
  }, [dispatch]);

  const revokeApiKey = useCallback(async () => {
    await API.apiKeyManagement.revokeApiKey({});
    showNotificationBanner(
      dispatch,
      "API Key Deleted!",
      NotificationType.NEGATIVE
    );
    setApiKeyId("");
  }, [dispatch]);

  const copyOnPress = useCallback(() => {
    if (!apiKeyId) {
      return;
    }
    navigator.clipboard
      .writeText(apiKeyId)
      .then(() => {
        showNotificationBanner(
          dispatch,
          "Copied API Key!",
          NotificationType.AFFIRMATIVE
        );
      })
      .catch((err) => {
        alert(err);
      });
  }, [apiKeyId, dispatch]);

  const dropdownItems: DropdownMenuItem[] = useMemo(() => {
    const items: DropdownMenuItem[] = [
      {
        title: "Regenerate",
        icon: "ion:reload",
        key: "regenerate",
        onPress: regenerateApiKey,
      },
      {
        title: "Delete API Key",
        icon: "ion:trash",
        key: "delete",
        isDestructive: true,
        onPress: () => setConfirmDelete(true),
      },
    ];

    return items;
  }, [regenerateApiKey]);

  return (
    <div className="ColumnNormal" style={{ margin: "auto", gap: 24 }}>
      <StandardBorderedContainer
        containerStyles={{
          width: 544,
          margin: "auto",
          paddingBlock: 14,
          borderRadius: 12,
        }}
      >
        <div
          className="AlignedRowSelect"
          style={{ gap: 4, flexWrap: "wrap", paddingInline: 14 }}
          onClick={goToMcpTab}
        >
          <span className="bodySubtext">Using an AI tool like Claude?</span>
          <span className="bodyMedium">Connect it with MCP instead</span>
          <Icon icon="ion:arrow-forward" height={14} />
        </div>
      </StandardBorderedContainer>
      <StandardBorderedContainer
        className="onboarding-mcp-api-key-target"
        containerStyles={{
          width: 544,
          margin: "auto",
          backgroundColor: Colors.WHITE,
          paddingBlock: 14,
        }}
      >
        <div className="ColumnNormal" style={{ gap: 7, paddingInline: 14 }}>
          <span className="sectionTitle">API Key</span>
          <span className="smallBodySubtext">
            Enable your application with texts and interact with Markit through
            API endpoints. Keeping your API key private is essential to protect
            your data and activity.
          </span>
        </div>
        <HorizontalDivider altMargin={14} />
        {accountData.hasApiKey ? (
          <div className="ColumnNormal" style={{ gap: 10, paddingInline: 14 }}>
            <div className="AlignedRow" style={{ gap: 10 }}>
              <CustomTextField
                inputMode={showApiKey ? "text" : "password"}
                value={apiKeyId || "xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"}
                placeholder=""
                onChange={() => {}}
                endAdornment={
                  apiKeyId ? (
                    <IconButton
                      aria-label="toggle key visibility"
                      onClick={handleClickShowApiKey}
                    >
                      <Icon
                        icon={showApiKey ? "mdi:eye-off" : "mdi:eye"}
                        height={20}
                        color={Colors.BLACK}
                      />
                    </IconButton>
                  ) : undefined
                }
                disabled
                width="337px"
                containerStyles={{
                  borderRadius: 100,
                  height: 42,
                  justifyContent: "center",
                }}
              />
              <div className="AlignedRow" style={{ gap: 7, flex: 1 }}>
                {!apiKeyId ? (
                  <LabelButton
                    text={"Hidden"}
                    icon={
                      <Icon
                        icon="ion:eye-off"
                        height={16}
                        color={Colors.GRAY1}
                      />
                    }
                    color={Colors.GRAY1}
                    backgroundColor={Colors.WHITE1}
                    containerStyles={{ padding: 14 }}
                  />
                ) : null}
                <DropdownMenu
                  dropdownItems={dropdownItems}
                  placement="bottomRight"
                  trigger={["click"]}
                  altWidth={200}
                  isOpen={isOpenDropdown}
                  onOpenChange={(isOpen: boolean) => setIsOpenDropdown(isOpen)}
                >
                  <RectangleButton
                    buttonLabel={"Manage"}
                    onPress={() => {}}
                    altColor={Colors.WHITE1}
                    altTextColor={Colors.BLACK}
                    containerStyles={{
                      padding: 14,
                      border: `0.5px solid ${Colors.GRAY2}`,
                    }}
                    iconRight={
                      <DropdownArrowIcon
                        isOpen={isOpenDropdown}
                        color={Colors.BLACK}
                        height={14}
                      />
                    }
                  />
                </DropdownMenu>
                {apiKeyId ? (
                  <RectangleButton
                    buttonLabel={"Copy"}
                    onPress={copyOnPress}
                    altColor={Colors.BLACK}
                    altTextColor={Colors.WHITE}
                    containerStyles={{ padding: 14 }}
                  />
                ) : null}
              </div>
            </div>
            {apiKeyId ? (
              <StandardBorderedContainer
                containerStyles={styles.noticeContainer}
              >
                <div className="AlignedRow" style={{ gap: 10 }}>
                  <Icon
                    icon="ion:warning"
                    height={24}
                    style={{ color: Colors.ORANGE1 }}
                  />
                  <span
                    className="bodySmallMedium"
                    style={{ color: Colors.ORANGE1 }}
                  >
                    Make sure to copy your API key now - you won&apos;t be able
                    to see it again. Store it somewhere safe.
                  </span>
                </div>
              </StandardBorderedContainer>
            ) : null}
          </div>
        ) : !hasSubscriptionUnlockedAdvancedData(accountData) ? (
          <EmptyStateButtonSubscription
            planType={PlanTypeV4.PRO}
            iconName={"ion:key"}
            btnText={
              hasSubscription(accountData) ? "Upgrade to Pro" : "Get Pro Plan"
            }
            containerStyles={{ paddingBlock: 30 }}
          />
        ) : (
          <div className="Centering" style={{ paddingBlock: 30 }}>
            <RectangleButton
              buttonLabel={"Generate"}
              onPress={generateApiKey}
              altColor={Colors.BLACK}
              altTextColor={Colors.WHITE}
              iconRight={
                <Icon icon="ion:key" height={16} color={Colors.WHITE} />
              }
              loading={loadingGenerate}
              width={118}
              containerStyles={{ borderRadius: 100, padding: 14 }}
            />
          </div>
        )}
      </StandardBorderedContainer>

      {showMcpTourFinalStep ? (
        <OnboardingTourStep
          key="mcp-api-tour-step-3"
          flowType={OnboardingFlowType.MCP_API}
          target=".onboarding-mcp-api-key-target"
          content={
            hasSubscriptionUnlockedAdvancedData(accountData)
              ? "Building your own app instead? Generate an API key here to use the Markit API and webhooks."
              : "Building your own app instead? The Markit API and webhooks are available on Pro and higher."
          }
          stepNumber={3}
          totalSteps={MCP_API_ONBOARDING_STEPS}
          placement="right"
        />
      ) : null}
      <ConfirmDeleteModal
        heading="Are you sure you want to delete this API key?"
        subtext="You cannot undo this."
        deleteButtonText={"Delete"}
        hideModal={!confirmDelete}
        setIsVisible={setConfirmDelete}
        deleteOnPress={revokeApiKey}
      />
    </div>
  );
};

export default ApiManagementPanel;
